import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { useUser } from "../../context/UserContext";
import { getRoleNames, hasAnyPermission } from "../../utils/tenantAdminAccess";
import {
  TENANT_ADMIN_WORKFLOWS,
  getWorkflowDetailPath,
} from "./workflowCatalog";
import {
  STATUS_META,
  getStatusLabel,
  resolveWorkflowStatus,
} from "./tenantAdminStatus";
import TenantAdminSectionHeader from "./TenantAdminSectionHeader";

function canViewWorkflow(workflow, user) {
  const roleNames = new Set(getRoleNames(user));
  if (roleNames.has("tenantadmin") || roleNames.has("platformadmin")) {
    return true;
  }

  if (!roleNames.has("projectleider")) {
    return false;
  }

  if (!Array.isArray(workflow?.permissionsAny) || workflow.permissionsAny.length === 0) {
    return true;
  }

  return hasAnyPermission(user, workflow.permissionsAny);
}

function WorkflowCard({ workflow, status }) {
  const meta = STATUS_META[status] || STATUS_META.concept;

  return (
    <article className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900">{workflow.title}</h3>
        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${meta.badge}`}>
          {getStatusLabel(status)}
        </span>
      </div>
      <p className="mb-4 text-sm text-slate-600">{workflow.description}</p>
      <Link
        to={getWorkflowDetailPath(workflow.slug)}
        className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        {meta.cta}
      </Link>
    </article>
  );
}

export default function TenantAdminWorkflowsPage() {
  const { user } = useUser();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;

    async function loadSettings() {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/tenant/settings");
        if (!alive) return;
        setSettings(res?.data || null);
      } catch (err) {
        if (!alive) return;
        setSettings(null);
        setError(err?.response?.data?.message || "Tenant settings konden niet worden geladen.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadSettings();
    return () => {
      alive = false;
    };
  }, []);

  const visibleWorkflows = useMemo(
    () => TENANT_ADMIN_WORKFLOWS.filter((workflow) => canViewWorkflow(workflow, user)),
    [user]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-white p-4 md:p-6">
      <div className="mx-auto max-w-6xl">
        <TenantAdminSectionHeader
          eyebrow="Werkprocessen"
          title="Werkprocessen"
          description="Beheer werkbontypes, workflows en procesinstellingen voor dit bedrijf."
        />

        {loading ? <p className="text-sm text-slate-600">Tenant instellingen laden...</p> : null}
        {!loading && error ? (
          <p className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            {error}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibleWorkflows.map((workflow) => (
            <WorkflowCard
              key={workflow.key}
              workflow={workflow}
              status={resolveWorkflowStatus(workflow.key, settings)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
